import React, { useState, useEffect } from 'react';
import { User } from '../types/user';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { AlertCircle, User as UserIcon, Phone, Mail, Calendar, Clock } from 'lucide-react';

interface ProfileStats {
  totalAppointments: number;
  upcomingAppointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
}

const fetchProfileStats = async (): Promise<ProfileStats> => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/stats`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch profile stats');
  }

  return response.json();
};

interface ProfilePageProps {
  user: User;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ user }) => {
  const [stats, setStats] = useState<ProfileStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await fetchProfileStats();
        setStats(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile stats');
      } finally {
        setIsLoading(false);
      }
    };

    loadStats();
  }, []);

  const StatCard = ({ title, value, icon: Icon }: { title: string; value: number; icon: any }) => (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-[#00c4cc] bg-opacity-10 rounded-lg">
          <Icon className="h-5 w-5 text-[#00c4cc]" />
        </div>
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-xl font-semibold">{value}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto p-4 animate-fadeIn">
      <h1 className="text-2xl font-bold mb-6">Mon profil</h1>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* Profile Information */}
        <section className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            Informations personnelles
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-500">Nom</label>
              <p className="font-medium">{user.firstName}</p>
            </div>
            
            <div>
              <label className="text-sm text-gray-500">Email</label>
              <p className="font-medium flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-400" />
                {user.email}
              </p>
            </div>
            
            {user.phoneNumber && (
              <div>
                <label className="text-sm text-gray-500">Téléphone</label>
                <p className="font-medium flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  {user.phoneNumber}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Stats Grid */}
        {stats && (
          <section className="grid grid-cols-2 gap-4">
            <StatCard
              title="Rendez-vous à venir"
              value={stats.upcomingAppointments}
              icon={Calendar}
            />
            <StatCard
              title="Consultations terminées"
              value={stats.completedAppointments}
              icon={Clock}
            />
          </section>
        )}

        {/* Actions */}
        <section className="space-y-3">
          <Button 
            variant="outline"
            className="w-full text-red-600 hover:text-red-700"
            onClick={() => {
              if (window.confirm('Êtes-vous sûr de vouloir supprimer votre compte ?')) {
                // TODO: Implement account deletion
              }
            }}
          >
            Supprimer mon compte
          </Button>
        </section>
      </div>
    </div>
  );
};